import { ClassificationProgressEvent, ClassificationResponse } from "src/types";

/**
 * Parses an NDJSON response stream and resolves with the payload
 * from the ``complete`` event.
 *
 * When an ``onProgress`` callback is provided, progress events
 * (``type: "progress"``) are forwarded to it.  Error events cause
 * the returned promise to reject.
 *
 * @param response - The fetch Response whose body is an NDJSON stream
 * @param onProgress - Optional callback for progress heartbeats
 * @returns The {@link ClassificationResponse} from the ``complete`` event
 */
export async function parseNdjsonStream(
  response: Response,
  onProgress?: (event: ClassificationProgressEvent) => void
): Promise<ClassificationResponse> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("Response body is not readable");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (value) {
        buffer += decoder.decode(value, { stream: true });
      }

      // Process all complete lines in the buffer
      let newlineIndex: number;
      while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);

        if (line.length === 0) {
          continue;
        }

        const event = JSON.parse(line);

        if (event.type === "complete") {
          return event.data as ClassificationResponse;
        }

        if (event.type === "progress") {
          onProgress?.(event as ClassificationProgressEvent);
          continue;
        }

        if (event.type === "error") {
          throw new Error(event.detail ?? "Classification failed");
        }
      }

      if (done) {
        break;
      }
    }
  } finally {
    reader.releaseLock();
  }

  throw new Error("Stream ended without a complete event");
}
